import { createContext, useContext, useEffect, useState } from "react";
import type {
  AITrace,
  AITraceEntry,
  AITraceSection,
  AITraceUsage,
} from "../api/models/aiTrace";
import { SELECTABLE_LABEL_STYLE } from "./selectableButton";
import { Chevron } from "./Chevron";

// Reusable viewer for a complete AI interaction trace (CLAUDE.md principle 12).
// Renders the summary header + every step in wire order - system prompt, tool
// catalogue, user prompt, thinking, text, tool calls/results, and the internal
// vision/tool model sub-calls - with per-entry collapse and recursive
// nested-section collapse so a large file read or the full system prompt stays
// manageable. Each step shows its own in/out token cost so an operator can find
// which part of an interaction was expensive. Presentational only (no API
// calls); fed by the useAiTrace hook through ViewAiTraceButton. Content is shown
// as preformatted text (no markdown dep). Ported from BMA's AITraceViewer.

const TYPE_META: Record<
  string,
  { label: string; color: string; collapsed: boolean }
> = {
  // Pre-expand the two entries that carry the headline debugging signal - what
  // the model was asked (user_prompt) and what it concluded (text/Assistant) -
  // and leave the noisier entries (system prompt, tool catalogue, thinking,
  // tool calls + results) collapsed so the operator drills into them on demand.
  // Routing decision is pre-expanded: it's the record of WHY this agent was
  // chosen (the router is deterministic, so there's no separate router trace).
  routing: { label: "Routing decision", color: "#0d9488", collapsed: false },
  system_prompt: { label: "System prompt", color: "#6d28d9", collapsed: true },
  tool_catalogue: { label: "Tools available", color: "#6d28d9", collapsed: true },
  user_prompt: { label: "User prompt", color: "#2563eb", collapsed: false },
  thinking: { label: "Thinking", color: "#b45309", collapsed: true },
  text: { label: "Assistant", color: "#111827", collapsed: false },
  tool_use: { label: "Tool call", color: "#0369a1", collapsed: true },
  tool_result: { label: "Tool result", color: "#0f766e", collapsed: true },
  tool_model_call: { label: "Model sub-call", color: "#9333ea", collapsed: true },
  turn_budget: { label: "Turn budget", color: "#b45309", collapsed: true },
  context_budget: { label: "Context budget", color: "#b45309", collapsed: true },
  conversation_history: {
    label: "Conversation history",
    color: "#0d9488",
    collapsed: true,
  },
  // Expanded: why the turn has no answer is the first thing to read.
  turn_failed: { label: "Turn failed", color: "#b91c1c", collapsed: false },
};

// The "`: name` (+ ` — target`)" suffix appended to an entry heading. Skips the
// name when the entry's label already ends with it (older traces baked the tool
// name into the tool_result label, which would otherwise render twice), and
// appends the document a file tool acted on when known.
const entryNameSuffix = (entry: AITraceEntry, labelText: string): string => {
  const name =
    entry.name &&
    entry.type !== "tool_model_call" &&
    !labelText.endsWith(`: ${entry.name}`)
      ? `: ${entry.name}`
      : "";
  const target = entry.target ? ` — ${entry.target}` : "";
  return `${name}${target}`;
};

// A merged trace (load_traces_merged) concatenates several persisted trace parts
// that fold into one /costs row, separating each with this marker entry. It is a
// part-group header, NOT a step - it and its part's entries are rendered by
// PartGroup (see groupEntries).
const DIVIDER_TYPE = "trace_part_divider";

const monospace: React.CSSProperties = {
  fontFamily: "monospace",
  fontSize: "11px",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  userSelect: "text",
};

// Expand/collapse-all signal. A bumped `nonce` forces every Disclosure to the
// given `open` state; after that the operator can still toggle nodes
// individually (the effect only re-runs when the nonce changes).
type ForceSignal = { open: boolean; nonce: number } | null;
const ForceContext = createContext<ForceSignal>(null);

// Suppress a toggle when the click is the tail of a drag text-selection, so an
// operator can expand the nodes they care about and then select across the
// visible trace to copy/paste it (e.g. into Claude) without collapsing things.
const isSelecting = (): boolean => {
  const sel = window.getSelection();
  return !!sel && !sel.isCollapsed && sel.toString().length > 0;
};

const Disclosure = ({
  summary,
  color,
  defaultOpen,
  children,
}: {
  summary: React.ReactNode;
  color?: string;
  defaultOpen: boolean;
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const force = useContext(ForceContext);
  // Re-run only when the nonce changes (not on first mount, where nonce is 0
  // and matches the seed), so expand-all / collapse-all overrides local state
  // without clobbering it on every render.
  useEffect(() => {
    if (force) setOpen(force.open);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [force?.nonce]);
  return (
    <div style={{ marginBottom: 4 }}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => {
          if (isSelecting()) return;
          setOpen((o) => !o);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
        style={{
          cursor: "pointer",
          color: color ?? "#334155",
          fontWeight: 600,
          fontSize: "11px",
          userSelect: "text",
        }}
      >
        <span style={{ userSelect: "none" }}><Chevron tree open={open} /> </span>
        {summary}
      </div>
      {open && <div style={{ marginLeft: 12, marginTop: 4 }}>{children}</div>}
    </div>
  );
};

const lineCountLabel = (section: AITraceSection): string => {
  const n =
    section.lineCount ??
    (section.content ? section.content.split("\n").length : 0);
  return n ? ` (${n.toLocaleString()} line${n === 1 ? "" : "s"})` : "";
};

const SectionView = ({ section }: { section: AITraceSection }) => {
  const hasChildren = !!section.sections && section.sections.length > 0;
  const summary = (
    <>
      {section.label}
      {lineCountLabel(section)}
      {section.truncated ? (
        <span style={{ color: "#b45309" }}> ⚠ trimmed</span>
      ) : null}
    </>
  );
  return (
    <Disclosure
      summary={summary}
      defaultOpen={!section.defaultCollapsed && !hasChildren}
    >
      {hasChildren ? (
        section.sections!.map((child, i) => (
          <SectionView key={i} section={child} />
        ))
      ) : (
        <div style={monospace}>{section.content}</div>
      )}
    </Disclosure>
  );
};

const fmt = (n: number): string => n.toLocaleString();

// Per-entry token cost, shown in the entry header so the expensive steps of an
// interaction stand out.
const tokens = (u?: AITraceUsage | null): string => {
  if (!u) return "";
  const bits: string[] = [];
  if (u.input_tokens || u.output_tokens) {
    bits.push(`${fmt(u.input_tokens ?? 0)} in / ${fmt(u.output_tokens ?? 0)} out`);
  }
  if (u.cache_read_input_tokens)
    bits.push(`${fmt(u.cache_read_input_tokens)} cache-read`);
  if (u.cache_creation_input_tokens)
    bits.push(`${fmt(u.cache_creation_input_tokens)} cache-write`);
  return bits.length ? ` · ${bits.join(" · ")}` : "";
};

// Short model label ("…claude-opus-4-8" -> "opus-4-8") for the per-turn meta line.
const shortModel = (m: string): string => m.replace(/^.*claude-/, "").replace(/-v\d+:\d+$/, "");

const timing = (e: AITraceEntry): string => {
  const parts: string[] = [];
  // Which model served this turn - shown per stage so a map+reduce (or a throttle
  // fallback) run makes clear which stage ran on which model.
  if (e.model_id) parts.push(shortModel(e.model_id));
  if (typeof e.modelSeconds === "number") parts.push(`model ${e.modelSeconds}s`);
  if (typeof e.toolSeconds === "number") parts.push(`tool ${e.toolSeconds}s`);
  if (typeof e.atSeconds === "number") parts.push(`@${e.atSeconds}s`);
  return parts.length ? ` · ${parts.join(" · ")}` : "";
};

// Which agent produced this step. One trace holds several agents once a
// delegation nests under the tool call that made it, so the chip is what makes
// "this is the Manager, that is the Grid specialist" readable at a glance.
// Renders nothing when the entry carries no agent (older traces, single-shot
// builders) rather than a placeholder.
const AgentChip = ({ agent }: { agent?: string | null }) =>
  agent ? (
    <span
      style={{
        ...SELECTABLE_LABEL_STYLE,
        display: "inline-block",
        background: "#e2e8f0",
        color: "#334155",
        borderRadius: 3,
        padding: "0 4px",
        marginRight: 5,
        fontWeight: 600,
        fontSize: "10px",
      }}
    >
      {agent}
    </span>
  ) : null;

// A delegated / spawned agent's own complete trace, nested under the tool call
// that made it. Its `atSeconds` are its own 0-based clock (the parent's timeline
// and header totals are deliberately untouched), so the header says so.
const NestedTrace = ({ trace }: { trace: AITrace }) => (
  <div
    style={{
      borderLeft: "2px solid #c4b5fd",
      paddingLeft: 8,
      marginTop: 6,
      marginBottom: 4,
    }}
  >
    <Disclosure
      summary={
        <span style={{ color: "#6d28d9" }}>
          Sub-agent trace · {usageSummary(trace)} · own timeline
        </span>
      }
      color="#6d28d9"
      defaultOpen={false}
    >
      <TraceEntries entries={trace.entries} />
    </Disclosure>
  </div>
);

const EntryView = ({ entry }: { entry: AITraceEntry }) => {
  const meta = TYPE_META[entry.type ?? ""] ?? {
    label: entry.type ?? "step",
    color: "#334155",
    collapsed: false,
  };
  // A problem read (raised / blank / garbled / truncated): flag the COLLAPSED
  // header with ⚠️ + the short reason so it's findable when skimming, and show
  // the full reason + detail inline in the (expanded) body.
  const hasIssue = !!entry.isError || !!entry.issueReason;
  const issueReason = entry.issueReason ?? (entry.isError ? "error" : null);
  const issueColor = "#b91c1c";
  const labelText = entry.label ?? meta.label;
  const heading = `${hasIssue ? "⚠️ " : ""}${labelText}${entryNameSuffix(
    entry,
    labelText,
  )}${hasIssue && issueReason ? ` — ${issueReason}` : ""}${tokens(entry.usage)}${timing(entry)}`;
  const hasSections = !!entry.sections && entry.sections.length > 0;

  const issueNote = hasIssue ? (
    <div style={{ color: issueColor, fontWeight: 600, marginBottom: 6 }}>
      ⚠️ {issueReason}
      {entry.issueDetail ? `: ${entry.issueDetail}` : ""}
    </div>
  ) : null;

  // tool_use: render the input JSON; otherwise the content (or nested sections).
  const inner = hasSections ? (
    entry.sections!.map((s, i) => <SectionView key={i} section={s} />)
  ) : entry.type === "tool_use" ? (
    <div style={monospace}>{JSON.stringify(entry.input ?? {}, null, 2)}</div>
  ) : (
    <div style={{ ...monospace, color: entry.isError ? issueColor : undefined }}>
      {entry.content}
    </div>
  );
  const nested = entry.nestedTrace;

  return (
    <Disclosure
      summary={
        <span style={{ color: hasIssue ? issueColor : meta.color }}>
          <AgentChip agent={entry.agent} />
          {heading}
        </span>
      }
      color={hasIssue ? issueColor : meta.color}
      defaultOpen={!meta.collapsed}
    >
      {issueNote}
      {inner}
      {nested && nested.entries && nested.entries.length > 0 ? (
        <NestedTrace trace={nested} />
      ) : null}
    </Disclosure>
  );
};

// A merged trace is a flat entry list with trace_part_divider markers. Group the
// entries by part so each part renders as ONE collapsible node its entries nest
// under (the divider is a group header, not a sibling step). A single-part
// (unmerged) trace has no dividers, so every entry comes back as a standalone
// "flat" item and renders exactly as before.
type TraceGroup =
  | { kind: "flat"; entry: AITraceEntry }
  | { kind: "part"; divider: AITraceEntry; entries: AITraceEntry[] };

const groupEntries = (entries: AITraceEntry[]): TraceGroup[] => {
  const out: TraceGroup[] = [];
  let current: Extract<TraceGroup, { kind: "part" }> | null = null;
  for (const entry of entries) {
    if (entry.type === DIVIDER_TYPE) {
      current = { kind: "part", divider: entry, entries: [] };
      out.push(current);
    } else if (current) {
      current.entries.push(entry);
    } else {
      // Entries before the first divider (none in practice - merge_traces always
      // opens with a divider - but render them rather than drop them).
      out.push({ kind: "flat", entry });
    }
  }
  return out;
};

const partSummary = (divider: AITraceEntry): React.ReactNode => (
  <span style={{ color: "#475569", fontWeight: 700 }}>
    {divider.label ?? "Trace part"}
    {tokens(divider.usage)}
    {typeof divider.atSeconds === "number"
      ? ` · starts @${divider.atSeconds}s`
      : ""}
  </span>
);

// One part of a merged trace: a collapsible header (the divider's label + the
// part's own token subtotal + its start offset on the stacked timeline) that its
// entries nest under, so an operator can collapse a whole part (e.g. hide a long
// OCR transcription) and focus on another.
const PartGroup = ({
  divider,
  entries,
}: {
  divider: AITraceEntry;
  entries: AITraceEntry[];
}) => (
  <div style={{ borderTop: "1px solid #cbd5e1", marginTop: 10, paddingTop: 6 }}>
    <Disclosure summary={partSummary(divider)} color="#475569" defaultOpen>
      {entries.map((e, i) => (
        <EntryView key={i} entry={e} />
      ))}
    </Disclosure>
  </div>
);

// One trace's entry list, grouped into parts. Shared by the top-level trace and
// by every nested sub-agent trace, so a delegation renders exactly like the run
// that made it.
const TraceEntries = ({ entries }: { entries: AITraceEntry[] }) => (
  <>
    {groupEntries(entries).map((g, i) =>
      g.kind === "part" ? (
        <PartGroup key={i} divider={g.divider} entries={g.entries} />
      ) : (
        <EntryView key={i} entry={g.entry} />
      ),
    )}
  </>
);

const usageSummary = (trace: AITrace): string => {
  const u = trace.usage ?? {};
  const bits: string[] = [];
  if (typeof trace.totalSeconds === "number")
    bits.push(`${trace.totalSeconds}s`);
  // Dividers are separators, not steps - exclude them so the count matches the
  // number of real interactions (a 2-part merge shows "6 steps", not "8").
  const steps = trace.entries.filter((e) => e.type !== DIVIDER_TYPE).length;
  bits.push(`${steps} steps`);
  if (u.input_tokens || u.output_tokens) {
    bits.push(`${fmt(u.input_tokens ?? 0)} in / ${fmt(u.output_tokens ?? 0)} out tokens`);
  }
  if (u.cache_read_input_tokens)
    bits.push(`${fmt(u.cache_read_input_tokens)} cache-read`);
  if (trace.model_id) bits.push(trace.model_id);
  if (trace.stop_reason) bits.push(`stop: ${trace.stop_reason}`);
  return bits.join(" · ");
};

// Flatten the whole trace to plain text - every entry + every nested section's
// full content, regardless of collapse state - for the Copy button. Headings
// use markdown-ish prefixes so the dumped trace stays readable when pasted
// (e.g. into Claude or a ticket).
const sectionToText = (s: AITraceSection, depth: number): string => {
  const head = `${"  ".repeat(depth)}## ${s.label}`;
  if (s.sections && s.sections.length) {
    return [head, ...s.sections.map((c) => sectionToText(c, depth + 1))].join("\n");
  }
  return `${head}\n${s.content ?? ""}`;
};

const indentBlock = (text: string): string =>
  text
    .split("\n")
    .map((line) => `    ${line}`)
    .join("\n");

const entryToText = (e: AITraceEntry): string => {
  if (e.type === DIVIDER_TYPE) {
    const at =
      typeof e.atSeconds === "number" ? ` · starts @${e.atSeconds}s` : "";
    return `## ${e.label ?? "Trace part"}${tokens(e.usage)}${at}`;
  }
  const meta = TYPE_META[e.type ?? ""] ?? { label: e.type ?? "step" };
  const labelText = e.label ?? meta.label;
  const hasIssue = !!e.isError || !!e.issueReason;
  const issueReason = e.issueReason ?? (e.isError ? "error" : null);
  const flag = hasIssue ? "⚠️ " : "";
  const who = e.agent ? `[${e.agent}] ` : "";
  const heading = `### ${flag}${who}${labelText}${entryNameSuffix(e, labelText)}${tokens(e.usage)}${timing(e)}`;
  const issueLine = hasIssue
    ? `⚠️ ${issueReason}${e.issueDetail ? `: ${e.issueDetail}` : ""}\n`
    : "";
  const body =
    e.sections && e.sections.length
      ? e.sections.map((s) => sectionToText(s, 0)).join("\n")
      : e.type === "tool_use"
        ? JSON.stringify(e.input ?? {}, null, 2)
        : (e.content ?? "");
  // A nested sub-agent trace is part of what the operator is copying (usually
  // the whole reason they opened the trace), indented so the boundary is clear.
  const nested =
    e.nestedTrace && e.nestedTrace.entries && e.nestedTrace.entries.length
      ? `\n\n#### Sub-agent trace (own timeline)\n${indentBlock(traceToText(e.nestedTrace))}`
      : "";
  return `${heading}\n${issueLine}${body}${nested}`;
};

const traceToText = (trace: AITrace): string =>
  [usageSummary(trace), ...trace.entries.map(entryToText)].join("\n\n");

const toolbarButton: React.CSSProperties = {
  background: "none",
  border: "1px solid #cbd5e1",
  borderRadius: 4,
  padding: "1px 8px",
  cursor: "pointer",
  color: "#334155",
  fontSize: "11px",
};

export const AiTraceViewer = ({ trace }: { trace: AITrace }) => {
  const [force, setForce] = useState<ForceSignal>(null);
  const [copied, setCopied] = useState<"idle" | "ok" | "err">("idle");
  // Reset the Copy button label after a moment; cleared on unmount so it
  // never sets state on an unmounted modal.
  useEffect(() => {
    if (copied === "idle") return;
    const t = setTimeout(() => setCopied("idle"), 1500);
    return () => clearTimeout(t);
  }, [copied]);
  const copyAll = () => {
    navigator.clipboard
      .writeText(traceToText(trace))
      .then(() => setCopied("ok"))
      .catch(() => setCopied("err"));
  };
  if (!trace || !trace.entries || trace.entries.length === 0) {
    return (
      <div style={{ ...monospace, color: "#64748b" }}>No trace available.</div>
    );
  }
  const setAll = (open: boolean) =>
    setForce((f) => ({ open, nonce: (f?.nonce ?? 0) + 1 }));
  return (
    <div
      style={{
        background: "rgba(0,0,0,0.04)",
        borderRadius: 4,
        padding: "8px",
        maxHeight: "50vh",
        overflowY: "auto",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          marginBottom: 8,
        }}
      >
        <div style={{ color: "#475569", fontSize: "11px", userSelect: "text" }}>
          {usageSummary(trace)}
        </div>
        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
          <button type="button" style={toolbarButton} onClick={() => setAll(true)}>
            Expand all
          </button>
          <button type="button" style={toolbarButton} onClick={() => setAll(false)}>
            Collapse all
          </button>
          <button
            type="button"
            style={toolbarButton}
            onClick={copyAll}
            title="Copy the full trace (every step, fully expanded) to the clipboard"
          >
            {copied === "ok"
              ? "Copied!"
              : copied === "err"
                ? "Copy failed"
                : "Copy text"}
          </button>
        </div>
      </div>
      <ForceContext.Provider value={force}>
        <TraceEntries entries={trace.entries} />
      </ForceContext.Provider>
    </div>
  );
};

export default AiTraceViewer;
